import { observer } from 'mobx-react-lite'
import { ModalSheetContentBlock } from '@/components/ModalSheet'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { Form } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { extractMessageFromError } from '@/utils/error-util'
import { type CouponSelectionViewModel } from '@/features/jobs/drafting/CouponSelectionViewModel'
import { type CouponModalViewModel } from './CouponModalViewModel'
import {
  CouponList,
  CouponListItem,
} from '@/features/billing/components/CouponList'
import { type Coupon } from '@/features/billing/coupons/Coupon'
import { useTask } from '@/hooks/task-hooks'
import {
  CouponModal,
  CouponModalCodeInput,
  CouponModalCoupons,
  CouponModalNoCoupons,
} from '@/features/jobs/drafting/components/JobCreation/Submission/CouponModal'

export interface CouponModalContainerProps {
  vm: CouponModalViewModel
}

export const CouponModalContainer = observer(function CouponModalContainer({
  vm,
}: CouponModalContainerProps) {
  return (
    <CouponModal
      showing={vm.modal.showing}
      isPending={vm.isPending}
      onClose={vm.modal.close}
    >
      {vm.selectionVm && <CouponModalContentContainer vm={vm.selectionVm} />}
    </CouponModal>
  )
})

interface CouponModalContentContainerProps {
  vm: CouponSelectionViewModel
}

const CouponModalContentContainer = observer(
  function CouponModalContentContainer({
    vm,
  }: CouponModalContentContainerProps) {
    return vm.fetchCoupons.match({
      pending: () => <ContentLoading size="compact" />,
      rejected: (err) => <ContentError error={err} />,
      resolved: () => (
        <>
          {vm.coupons.length > 0 ? (
            <CouponModalCoupons count={vm.coupons.length}>
              <CouponList>
                {vm.coupons.map((coupon) => (
                  <CouponListItemContainer
                    key={coupon.id}
                    coupon={coupon}
                    vm={vm}
                  />
                ))}
              </CouponList>
            </CouponModalCoupons>
          ) : (
            <CouponModalNoCoupons />
          )}
          <AddCouponSectionContainer vm={vm} />
        </>
      ),
    })
  },
)

interface CouponListItemContainerProps {
  vm: CouponSelectionViewModel
  coupon: Coupon
}

const CouponListItemContainer = observer(function CouponListItemContainer({
  vm,
  coupon,
}: CouponListItemContainerProps) {
  const select = useTask.resolved(() => vm.select(coupon), [vm, coupon])
  const isSelected = vm.isSelected(coupon)
  return (
    <CouponListItem
      friendlyName={coupon.friendlyName}
      code={coupon.code}
      description={coupon.description}
      testId={`coupon-list-item-${coupon.code}`}
      selected={isSelected}
      onPress={isSelected ? undefined : select}
      isPending={select.pending}
      isDisabled={vm.select.pending || vm.disabled}
    />
  )
})

interface AddCouponSectionContainerProps {
  vm: CouponSelectionViewModel
}

const AddCouponSectionContainer = observer(function AddCouponSectionContainer({
  vm,
}: AddCouponSectionContainerProps) {
  const error = extractMessageFromError(vm.addCoupon.error)
  return (
    <ModalSheetContentBlock>
      <Form onSubmit={vm.canAdd ? vm.addCoupon : undefined}>
        <FormGroupContainer label="Add a new Coupon" error={error}>
          <CouponModalCodeInput
            code={vm.codeToAdd}
            onCodeChange={vm.setCode}
            hasError={!!error}
            isDisabled={vm.isPending || vm.disabled}
            canAdd={vm.canAdd}
            isAddPending={vm.addCoupon.pending}
          />
        </FormGroupContainer>
      </Form>
    </ModalSheetContentBlock>
  )
})
